import { ColoredChipIconButton } from '@components/colored-elements';
import { Box, Tooltip, Typography } from '@material-ui/core';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { useState } from 'react';
import { Column } from 'react-table';
import ContactDetailsModal from '../components/contact-details-modal';
import { ApolloEnrichedContact } from '../apollo-enriched-contacts.types';
import { DealFilter, StringFilters } from '../filters';
import { $contains, $eq } from '../filters/operators';
import { DbExistsMark, EmailCopy, EmailStatusChips } from './status';
import { addTitleToFilter } from './utils';

type GlobalFilterColumn<D extends object = Record<string, any>> = Column<D> & {
  primaryFilter?: boolean;
  primaryFilterOperator?: string;
  forceHide?: boolean;
  passiveColumn?: boolean;
  overrideOperator?: { [k: string]: Partial<DealFilter> };
  primaryFilterOrder?: number;
};

const ViewContactCell = ({ row }) => {
  const [open, setOpen] = useState(false);
  const contact = row.original as ApolloEnrichedContact;
  return (
    <>
      <Tooltip title="View">
        <ColoredChipIconButton
          pointer
          color="primary"
          size="medium"
          icon={<VisibilityIcon />}
          onClick={() => setOpen(true)}
        />
      </Tooltip>
      <ContactDetailsModal
        contactId={contact.id}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

const PersonNameCell = ({ row }) => {
  const contact = row.original as ApolloEnrichedContact;
  return (
    <Box>
      <Typography variant="body2" style={{ fontWeight: 600 }}>
        {contact.personName || '-'}
      </Typography>
      {contact.groupedCount > 1 ? (
        <Typography variant="caption" color="textSecondary">
          {contact.groupedCount} records
        </Typography>
      ) : null}
    </Box>
  );
};

export const EmailCell = ({ row }) => {
  const contact = row.original as ApolloEnrichedContact;
  return (
    <Box>
      {contact.email ? <EmailCopy value={contact.email} /> : <Typography variant="body2">-</Typography>}
      <Box mt={0.5}>
        <EmailStatusChips status={contact.emailStatus} />
      </Box>
    </Box>
  );
};

const DbExistsCell = ({ row }) => {
  const contact = row.original as ApolloEnrichedContact;
  return <DbExistsMark dbExists={contact.dbExists} />;
};

export const columns: GlobalFilterColumn<ApolloEnrichedContact>[] = [
  {
    accessor: 'id',
    Header: 'ID',
    disableFilters: true,
  },
  {
    id: 'person_name',
    accessor: 'personName',
    Header: 'Person Name',
    primaryFilter: true,
    primaryFilterOperator: $contains.key,
    primaryFilterOrder: 4,
    Filter: addTitleToFilter(StringFilters, 'Person Name'),
    Cell: PersonNameCell,
  },
  {
    id: 'designation',
    accessor: 'designation',
    Header: 'Designation',
    primaryFilter: true,
    primaryFilterOperator: $contains.key,
    primaryFilterOrder: 1,
    Filter: addTitleToFilter(StringFilters, 'Designation'),
  },
  {
    id: 'company',
    accessor: 'companyName',
    Header: 'Company',
    primaryFilter: true,
    primaryFilterOperator: $eq.key,
    primaryFilterOrder: 3,
    Filter: addTitleToFilter(StringFilters, 'Company'),
  },
  {
    id: 'email',
    accessor: 'email',
    Header: 'Email',
    primaryFilter: true,
    primaryFilterOperator: $contains.key,
    primaryFilterOrder: 2,
    Filter: addTitleToFilter(StringFilters, 'Email'),
    Cell: EmailCell,
  },
  {
    id: 'db_exists',
    Header: 'DB Exists',
    disableFilters: true,
    disableSortBy: true,
    Cell: DbExistsCell,
  },
  {
    id: 'location',
    accessor: 'location',
    Header: 'Location',
    Filter: addTitleToFilter(StringFilters, 'Location'),
  },
  {
    id: 'actions',
    Header: 'Action',
    disableFilters: true,
    disableSortBy: true,
    Cell: ViewContactCell,
  },
];
